import { RawAxiosRequestConfig } from "axios";

import { loginApi } from "src/api/taktileApi";
import { SessionScope } from "src/api/tokenStorage";

export type RefreshRequestConfig = RawAxiosRequestConfig & {
  // Marks the session recovery's own refresh POSTs, so the 401 middleware can
  // tell them apart from independent refresh callers (boot, proactive
  // near-expiry) that share the same endpoint.
  recoveryRefresh?: boolean;
};

/**
 * `scope` narrows the mint and is sealed on the new refresh token; omitted,
 * the sealed scope carries. Authenticates with the refresh token (no role
 * strings), so it works when the id_token exceeds the header limit.
 */
export const loginWithRefreshToken = async (
  options?: RefreshRequestConfig,
  scope?: SessionScope,
) => {
  return (
    await loginApi.authRefreshApiV1LoginRefreshPost(
      scope?.orgId,
      scope?.wsId,
      undefined, // host
      options,
    )
  ).data;
};

/**
 * Mint an (org, ws)-scoped session token. Pass the active role narrowing so
 * the new token keeps it — re-minting replaces the token wholesale.
 */
export const loginWithWorkspaceScope = async (
  organizationId: string,
  workspaceId: string,
  roles: string[] | undefined,
  flowFolderId: string | undefined,
  options?: { signal?: AbortSignal },
) => {
  return (
    await loginApi.loginAccessTokenApiV1LoginAccessTokenPost(
      undefined, // expiresSeconds
      organizationId,
      workspaceId,
      flowFolderId,
      roles?.length ? roles : undefined,
      undefined, // host
      options,
    )
  ).data;
};

/**
 * Re-mint the session token narrowed to `roles` (staff-only; can only narrow,
 * never widen). An empty array drops the narrowing and restores the full token.
 */
export const loginWithRoleScope = async (
  roles: string[],
  organizationId?: string,
  workspaceId?: string,
  flowFolderId?: string,
) => {
  return (
    await loginApi.loginAccessTokenApiV1LoginAccessTokenPost(
      undefined, // expiresSeconds
      organizationId,
      workspaceId,
      roles.length > 0 ? flowFolderId : undefined,
      roles.length > 0 ? roles : undefined,
    )
  ).data;
};

/**
 * Drops the (org, ws) scope. `roles` is unrelated to that scope and is carried
 * through unchanged.
 */
export const loginUnscoped = async (
  roles: string[] | undefined,
  flowFolderId: string | undefined,
  options?: { signal?: AbortSignal },
) => {
  return (
    await loginApi.loginAccessTokenApiV1LoginAccessTokenPost(
      undefined, // expiresSeconds
      undefined, // organizationId — omitted → unscoped
      undefined, // workspaceId
      flowFolderId,
      roles?.length ? roles : undefined,
      undefined, // host
      options,
    )
  ).data;
};

/**
 * Mint an (org, ws)-scoped id_token WITHOUT applying it to the session. Used to
 * read a cross-workspace source (e.g. copy-from-template) while the session
 * token is scoped to another workspace. Carries the session's role narrowing so
 * the cross-workspace read sees what the narrowed roles see, not more.
 */
export const mintSourceToken = async (
  organizationId: string,
  workspaceId: string,
  roles?: string[],
  flowFolderId?: string,
): Promise<string> => {
  return (
    await loginApi.loginAccessTokenApiV1LoginAccessTokenPost(
      undefined,
      organizationId,
      workspaceId,
      flowFolderId,
      roles?.length ? roles : undefined,
    )
  ).data.id_token;
};
